import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../firebase';
import { getOrgConfig } from '../utils/orgResolution';
import { apiRedeemSignInLink } from '../utils/rmsApi';
import { readEmailedSignInLink, safeRmsContinuePath } from '../utils/emailedSignInLink';

/*
 * Completes sign-in from an emailed RMS sign-in link (TDI-706 criterion 1).
 *
 * When the page address carries a link token (t=), swaps it for a custom
 * token once per page load, signs in with it, and goes on to the address the
 * server stored with the link (only an /rms/ path on this site; anything else
 * becomes defaultContinue). The token is single use, so a re-render or a
 * second effect run never sends it twice.
 *
 * Returns { link, status }: link is null when there is no token; status is
 * 'idle' (no token), 'redeeming' or 'failed' (expired, used or invalid).
 */
export default function useEmailedSignInLink({ defaultContinue, onError } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const link = useMemo(() => readEmailedSignInLink(location.search), [location.search]);
  const [status, setStatus] = useState(link ? 'redeeming' : 'idle');
  const attempted = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!link || attempted.current) return;
    attempted.current = true;
    setStatus('redeeming');
    const orgId = link.orgId || getOrgConfig().orgId;
    (async () => {
      try {
        const { customToken, continuePath } = await apiRedeemSignInLink({ orgId, token: link.token });
        await signInWithCustomToken(auth, customToken);
        navigate(safeRmsContinuePath(continuePath, defaultContinue), { replace: true });
      } catch (e) {
        setStatus('failed');
        if (onErrorRef.current) onErrorRef.current(e);
      }
    })();
  }, [defaultContinue, link, navigate]);

  return { link, status };
}
